/**
 * Closure / `within` module — the distance & reachability substrate the v0.10
 * selector core (`>>`, `<>`, cones) evaluates over. (Design §1, plan P0.2.)
 *
 * ## The vertex model
 *
 * The edge universe is TWO structurally-disjoint subgraphs:
 *   - **call** edges collapse to `module → module` (`fromModulePath` →
 *     `toModulePath`; the interface refinement is an endpoint-MATCH concern the
 *     evaluator resolves, not a distance concern);
 *   - **do** edges are `process → process` (`fromProcessId` → `toProcessId`).
 *
 * Vertices are string keys, prefixed `m:` / `p:`, so a module and a process can
 * never share a key — which is exactly why **call and do never compose across
 * kinds** (design OQ 12): no path can step from a call hop onto a do hop because
 * they share no vertex. Both live in one adjacency; the disjointness is
 * structural, not a special case.
 *
 * ## Distances
 *
 * Unweighted (every edge = 1 hop), multi-source BFS, memoized per anchor-set.
 *   - directed (`>>`): `dist(X,u) + 1 + dist(v,Y) ≤ N` for an edge `u → v`;
 *   - undirected (`<>`): SYMMETRIZED to include the reversed base case
 *     (`A <> B within 1` must include the edge `B → A`) —
 *     `min(dU(X,u)+dU(v,Y), dU(X,v)+dU(u,Y)) + 1 ≤ N`, distances direction-blind.
 * `N = Infinity` is the unbounded cone (`* >> Payments`).
 *
 * ## Performance
 *
 * Vertices are interned to dense integer ids and adjacency/BFS run over typed
 * arrays, so a full structure build + query clears the P0.2 budget (<5ms warm
 * re-eval at 10k edges — see `bench/closure.mjs`) WITHOUT needing per-dirty-SCC
 * incremental patching. Tarjan SCCs are still exposed for the delta-engine
 * invalidation contract (`dirtySccIds`) and cycle handling.
 *
 * ## Cycles
 *
 * `distancesFrom` anchors are distance 0 (the within formula's base). Cone
 * reachability (`reach`) uses ≥1-hop semantics, so a node reaches itself only
 * through a real cycle (design "self-reach ≥1 hop").
 */

import type { DependencyEdge, DependencyGraph } from "./graph.js";

export type ClosureMode = "directed" | "undirected";

/** Vertex key for a module (call-subgraph endpoint). */
export function moduleKey(path: readonly string[]): string {
  return `m:${path.join(".")}`;
}

/** Vertex key for a process/subprocess (do-subgraph endpoint). */
export function processKey(id: string): string {
  return `p:${id}`;
}

/** The two endpoint keys of an edge, `u → v` (directed). */
function edgeEndpoints(e: DependencyEdge): readonly [string, string] {
  return e.kind === "do"
    ? [processKey(e.fromProcessId), processKey(e.toProcessId)]
    : [moduleKey(e.fromModulePath), moduleKey(e.toModulePath)];
}

export interface QualifyOpts {
  /** Source anchor vertex keys (`X`). */
  readonly from: ReadonlySet<string>;
  /** Sink anchor vertex keys (`Y`). */
  readonly to: ReadonlySet<string>;
  /** Hop bound `N`; `Infinity` = unbounded cone. */
  readonly within: number;
  readonly mode: ClosureMode;
}

export interface Closure {
  /** All vertex keys in the universe (both subgraphs). */
  readonly nodeCount: number;
  hasNode(key: string): boolean;
  /**
   * Edges qualifying `X (>>|<>) Y within N`. `mode: "directed"` is `>>`,
   * `"undirected"` is the symmetrized `<>`. Returns the original edge objects,
   * in `graph.edges` order.
   */
  qualifyEdges(opts: QualifyOpts): DependencyEdge[];
  /**
   * Cone node set — vertices reachable from `anchors` (`downstream`) or that
   * reach `anchors` (`upstream`), via ≥1 hop (a node appears via itself only
   * through a cycle). Direction-blind when `mode === "undirected"`.
   */
  reach(anchors: ReadonlySet<string>, direction: "downstream" | "upstream", mode?: ClosureMode): Set<string>;
  /** Strongly-connected-component id of a vertex (−1 if unknown). */
  sccOf(key: string): number;
  /** SCC ids touched by a set of changed module paths — the delta-engine
   *  invalidation surface (a consumer drops memo entries over these). */
  dirtySccIds(changedModulePaths: Iterable<string>): Set<number>;
}

const INF = 0x7fffffff;

// =============================================================================
// Build
// =============================================================================

export function buildClosure(graph: DependencyGraph): Closure {
  // Intern every endpoint to a dense integer id. Isolated nodes never appear on
  // an edge, so they're irrelevant to distance/reach and are skipped.
  const id = new Map<string, number>();
  const keys: string[] = [];
  const intern = (k: string): number => {
    let i = id.get(k);
    if (i === undefined) {
      i = keys.length;
      id.set(k, i);
      keys.push(k);
    }
    return i;
  };

  // Per-edge endpoint ids (parallel to graph.edges) — computed once so the
  // per-query loop is pure integer lookups, not string joins.
  const eu = new Int32Array(graph.edges.length);
  const ev = new Int32Array(graph.edges.length);
  // Adjacency as id → id[] (fwd, rev, undirected). Dedup within a list is
  // unnecessary: BFS visits by first-seen, so parallel edges cost nothing.
  const fwd: number[][] = [];
  const rev: number[][] = [];
  const und: number[][] = [];
  const grow = (i: number) => {
    while (fwd.length <= i) {
      fwd.push([]);
      rev.push([]);
      und.push([]);
    }
  };
  for (let e = 0; e < graph.edges.length; e++) {
    const [uk, vk] = edgeEndpoints(graph.edges[e]!);
    const u = intern(uk);
    const v = intern(vk);
    eu[e] = u;
    ev[e] = v;
    grow(Math.max(u, v));
    fwd[u]!.push(v);
    rev[v]!.push(u);
    und[u]!.push(v);
    und[v]!.push(u);
  }
  const n = keys.length;

  const scc = tarjanScc(n, fwd);

  // Memoized multi-source BFS → Int32Array of distances (INF = unreachable).
  // Key = mode-tag + sorted anchor-id join; the anchor sets a policy/view
  // produces are stable across keystrokes, so warm re-eval hits the cache.
  const memo = new Map<string, Int32Array>();
  const bfs = (anchorIds: number[], adj: number[][], tag: string): Int32Array => {
    const sorted = anchorIds.slice().sort((a, b) => a - b);
    const key = `${tag}|${sorted.join(",")}`;
    const hit = memo.get(key);
    if (hit) return hit;
    const dist = new Int32Array(n).fill(INF);
    const queue = new Int32Array(n);
    let tail = 0;
    for (const a of sorted) if (dist[a] === INF) { dist[a] = 0; queue[tail++] = a; }
    for (let head = 0; head < tail; head++) {
      const cur = queue[head]!;
      const d = dist[cur]! + 1;
      for (const nxt of adj[cur]!) if (dist[nxt] === INF) { dist[nxt] = d; queue[tail++] = nxt; }
    }
    memo.set(key, dist);
    return dist;
  };

  const anchorIds = (keysIn: ReadonlySet<string>): number[] => {
    const out: number[] = [];
    for (const k of keysIn) {
      const i = id.get(k);
      if (i !== undefined) out.push(i);
    }
    return out;
  };

  return {
    nodeCount: n,
    hasNode: (k) => id.has(k),

    qualifyEdges({ from, to, within, mode }: QualifyOpts): DependencyEdge[] {
      const out: DependencyEdge[] = [];
      const fromIds = anchorIds(from);
      const toIds = anchorIds(to);
      if (fromIds.length === 0 || toIds.length === 0) return out;
      if (mode === "directed") {
        const dX = bfs(fromIds, fwd, "F");
        const dY = bfs(toIds, rev, "R");
        for (let e = 0; e < graph.edges.length; e++) {
          const du = dX[eu[e]!]!;
          const dv = dY[ev[e]!]!;
          // Both endpoints must be REACHABLE — an unbounded `within` (Infinity)
          // would otherwise pass `INF + 1 ≤ Infinity` for an off-path edge.
          if (du !== INF && dv !== INF && du + 1 + dv <= within) out.push(graph.edges[e]!);
        }
      } else {
        const uX = bfs(fromIds, und, "U");
        const uY = bfs(toIds, und, "U");
        for (let e = 0; e < graph.edges.length; e++) {
          const u = eu[e]!;
          const v = ev[e]!;
          const a = uX[u] !== INF && uY[v] !== INF ? uX[u]! + uY[v]! : INF;
          const b = uX[v] !== INF && uY[u] !== INF ? uX[v]! + uY[u]! : INF;
          const m = a < b ? a : b;
          if (m !== INF && m + 1 <= within) out.push(graph.edges[e]!);
        }
      }
      return out;
    },

    reach(anchors, direction, mode = "directed") {
      const adj = mode === "undirected" ? und : direction === "downstream" ? fwd : rev;
      const seen = new Uint8Array(n);
      const queue: number[] = [];
      // ≥1-hop reach: seed from the anchors' neighbours, so an anchor re-enters
      // the set only by completing a cycle back to itself.
      for (const a of anchorIds(anchors)) for (const nb of adj[a]!) {
        if (!seen[nb]) { seen[nb] = 1; queue.push(nb); }
      }
      for (let head = 0; head < queue.length; head++) {
        for (const nxt of adj[queue[head]!]!) if (!seen[nxt]) { seen[nxt] = 1; queue.push(nxt); }
      }
      const out = new Set<string>();
      for (let i = 0; i < n; i++) if (seen[i]) out.add(keys[i]!);
      return out;
    },

    sccOf: (k) => {
      const i = id.get(k);
      return i === undefined ? -1 : scc[i]!;
    },

    dirtySccIds(changedModulePaths) {
      const ids = new Set<number>();
      for (const p of changedModulePaths) {
        const key = moduleKey(Array.isArray(p) ? p : String(p).split("."));
        const i = id.get(key);
        if (i !== undefined) ids.add(scc[i]!);
      }
      return ids;
    },
  };
}

// =============================================================================
// Tarjan SCC (iterative — the graph can be deep; no recursion-depth risk)
// =============================================================================

function tarjanScc(n: number, fwd: readonly number[][]): Int32Array {
  const index = new Int32Array(n).fill(-1);
  const low = new Int32Array(n);
  const onStack = new Uint8Array(n);
  const comp = new Int32Array(n).fill(-1);
  const stack = new Int32Array(n);
  let sp = 0;
  let counter = 0;
  let compId = 0;

  // Iterative DFS: parallel frame stacks carry the node + a cursor into its
  // out-neighbour list, so we resume after recursing into a child.
  const wNode = new Int32Array(n);
  const wPos = new Int32Array(n);

  for (let root = 0; root < n; root++) {
    if (index[root] !== -1) continue;
    let wp = 0;
    wNode[wp] = root;
    wPos[wp] = 0;
    index[root] = counter;
    low[root] = counter;
    counter++;
    stack[sp++] = root;
    onStack[root] = 1;

    while (wp >= 0) {
      const node = wNode[wp]!;
      const neighbours = fwd[node]!;
      if (wPos[wp]! < neighbours.length) {
        const w = neighbours[wPos[wp]!]!;
        wPos[wp]!++;
        if (index[w] === -1) {
          index[w] = counter;
          low[w] = counter;
          counter++;
          stack[sp++] = w;
          onStack[w] = 1;
          wp++;
          wNode[wp] = w;
          wPos[wp] = 0;
        } else if (onStack[w]) {
          if (index[w]! < low[node]!) low[node] = index[w]!;
        }
      } else {
        // Done with `node` — close a component when it is a root (low == index),
        // else fold its low-link into the parent frame.
        if (low[node] === index[node]) {
          let w: number;
          do {
            w = stack[--sp]!;
            onStack[w] = 0;
            comp[w] = compId;
          } while (w !== node);
          compId++;
        }
        wp--;
        if (wp >= 0 && low[node]! < low[wNode[wp]!]!) low[wNode[wp]!] = low[node]!;
      }
    }
  }
  return comp;
}
